<template>
  <Teleport :to="store.overlayTarget">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="store.bookingSlideOverOpen"
        class="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-30"
        @click="store.closeBookingSlideOver()"
      ></div>
    </Transition>

    <!-- Panel -->
    <Transition name="slide-over">
      <div
        v-if="store.bookingSlideOverOpen"
        class="fixed inset-y-0 right-0 w-full sm:w-[460px] bg-white dark:bg-[#1C2128] shadow-2xl z-40 flex flex-col"
        @click.stop
      >
        <BookingSlideOverHeader
          :title="isEditMode ? 'Edit Booking' : 'New Booking'"
          :saving="saving"
          @close="store.closeBookingSlideOver()"
          @save="submit"
        />

        <!-- Error banner -->
        <div v-if="error" class="mx-4 mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
          {{ error }}
        </div>

        <!-- Form body -->
        <div class="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          <BookingTypeToggle v-if="!isEditMode" v-model="form.bookingType" />

          <!-- Internal booking fields -->
          <template v-if="form.bookingType === 'internal'">
            <BookingEventSelector
              v-model="form.eventId"
              :events="store.hostEvents"
              :loading="store.hostEventsLoading"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Booking title. Defaults to the event name."
                class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <BookingDateTimeRow
              v-model:date="form.date"
              v-model:start-time="form.startTime"
              v-model:end-time="form.endTime"
            />

            <BookingGuestInput
              :guests="form.guests"
              @update:guests="form.guests = $event"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Add a note..."
                class="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              ></textarea>
            </div>
          </template>

          <!-- External booking fields -->
          <template v-else>
            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="Event title"
                class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <BookingDateTimeRow
              v-model:date="form.date"
              v-model:start-time="form.startTime"
              v-model:end-time="form.endTime"
            />

            <div>
              <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                placeholder="Add a note..."
                class="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              ></textarea>
            </div>

            <p class="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">
              A plain entry on your own calendar. No guests are invited and nobody can book it.
            </p>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'
import { useGlobalStore }   from 'stores/global.js'
import { useRequest }       from 'composables/useRequest.js'
import BookingSlideOverHeader from './BookingSlideOverHeader.vue'
import BookingTypeToggle      from './BookingTypeToggle.vue'
import BookingEventSelector   from './BookingEventSelector.vue'
import BookingDateTimeRow     from './BookingDateTimeRow.vue'
import BookingGuestInput      from './BookingGuestInput.vue'

// No props — reads entirely from store
const store       = useCalendarStore()
const globalStore = useGlobalStore()
const { axiosRequest } = useRequest()

const saving = ref(false)
const error  = ref(null)

const isEditMode = computed(() => !!store.bookingSlideOverBooking?.id)

// ── Form state ─────────────────────────────────────────────────────────────────

// Local calendar date, not UTC. toISOString() rolls the date over at UTC
// midnight, which put the form on the wrong day for anyone west of Greenwich
// in the evening and anyone east of it early in the morning.
function localDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function localTimeStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function defaultDate() {
  return localDateStr(new Date())
}

function defaultTime(offsetMinutes = 0) {
  const d = new Date()
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15 + offsetMinutes, 0, 0)
  return localTimeStr(d)
}

const form = ref(blankForm())

function blankForm(slotDate = null, slotEndDate = null) {
  const base = slotDate ? new Date(slotDate) : null
  // A drag supplies its own end; a plain click gets the default one hour.
  const end  = slotEndDate ? new Date(slotEndDate)
             : base ? new Date(base.getTime() + 60 * 60000)
             : null
  return {
    // A plain calendar entry by default: it needs no event type, so a drag on
    // the grid goes straight to something saveable.
    bookingType: 'external',
    eventId:     '',
    name:        '',
    date:        base ? localDateStr(base) : defaultDate(),
    startTime:   base ? localTimeStr(base) : defaultTime(0),
    endTime:     end  ? localTimeStr(end)  : defaultTime(60),
    guests:      [],
    description: '',
  }
}

function buildFormFromBooking(booking) {
  const start = new Date(booking.start_at ?? booking.start)
  const end   = new Date(booking.end_at   ?? booking.end)
  return {
    bookingType: 'internal',
    eventId:     booking.event_id ?? '',
    name:        booking.title ?? booking.name ?? '',
    // Date and time both read in local time so they cannot disagree across
    // the UTC boundary.
    date:        localDateStr(start),
    startTime:   localTimeStr(start),
    endTime:     localTimeStr(end),
    guests:      [],
    description: booking.description ?? booking.body ?? '',
  }
}

// Reset/populate form whenever the slide-over opens
watch(() => store.bookingSlideOverOpen, (open) => {
  if (!open) return
  error.value  = null
  saving.value = false
  form.value   = store.bookingSlideOverBooking
    ? buildFormFromBooking(store.bookingSlideOverBooking)
    : blankForm(store.bookingSlideOverSlotDate, store.bookingSlideOverSlotEndDate)
})

function onKeydown(e) {
  if (e.key === 'Escape' && store.bookingSlideOverOpen) store.closeBookingSlideOver()
}
onMounted(()   => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

// ── Submit ─────────────────────────────────────────────────────────────────────

async function submit() {
  error.value = null

  if (form.value.bookingType === 'internal' && !form.value.eventId) {
    error.value = 'Please select an event type.'
    return
  }
  if (!form.value.date || !form.value.startTime || !form.value.endTime) {
    error.value = 'Please fill in the date and time.'
    return
  }
  if (form.value.endTime <= form.value.startTime) {
    error.value = 'End time must be after start time.'
    return
  }

  const startAt = `${form.value.date}T${form.value.startTime}:00`
  const endAt   = `${form.value.date}T${form.value.endTime}:00`

  saving.value = true

  try {
    if (isEditMode.value) {
      await submitUpdate(startAt, endAt)
    } else if (form.value.bookingType === 'internal') {
      await submitInternal(startAt, endAt)
    } else {
      await submitExternal(startAt, endAt)
    }
  } finally {
    saving.value = false
  }
}

// Edit mode used to POST to the create endpoint, which quietly produced a
// second booking every time someone edited one.
async function submitUpdate(startAt, endAt) {
  const id  = store.bookingSlideOverBooking.id
  const res = await axiosRequest('PUT', `/api/v1/bookings/${id}`, {
    booking: {
      name:        form.value.name || undefined,
      description: form.value.description || undefined,
      start_at:    startAt,
      end_at:      endAt,
    },
  }, false)

  if (!res.js_error && res.response?.success) {
    store.updateBooking({ ...store.bookingSlideOverBooking, ...res.response.data })
    store.closeBookingSlideOver()
  } else {
    error.value = res.response?.message || 'Could not save booking.'
  }
}

async function submitInternal(startAt, endAt) {
  // The API requires at least one attendee. A host blocking time on their own
  // calendar is that attendee — this used to send a synthetic
  // "host@placeholder.internal" address, which created a junk Attendee row and
  // surfaced as a guest on the booking.
  const guests = form.value.guests.length
    ? form.value.guests
    : [{ email: globalStore.user.email, full_name: globalStore.user.display_name }]

  const res = await axiosRequest('POST', '/api/v1/calendar/internal_bookings', {
    booking: {
      event_id:    form.value.eventId,
      start_at:    startAt,
      end_at:      endAt,
      name:        form.value.name || undefined,
      description: form.value.description || undefined,
      // Wall-clock times above carry no offset, so the server needs to be told
      // which zone to read them in.
      timezone:    globalStore.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    attendees: guests,
  }, false)

  if (!res.js_error && res.response?.success) {
    // Show it straight away rather than waiting on the ActionCable round trip;
    // addBooking is keyed by id, so the broadcast is a no-op re-set.
    if (res.response.data?.id) store.addBooking(res.response.data)
    store.closeBookingSlideOver()
  } else {
    error.value = res.response?.message || 'Could not save booking.'
  }
}

// A plain entry on your own calendar: no event type, no attendee, no booking
// page. This is the "just put something on my calendar" path.
async function submitExternal(startAt, endAt) {
  const res = await axiosRequest('POST', '/api/v1/calendar/personal_bookings', {
    booking: {
      name:        form.value.name || undefined,
      description: form.value.description || undefined,
      start_at:    startAt,
      end_at:      endAt,
      timezone:    globalStore.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  }, false)

  if (!res.js_error && res.response?.success) {
    if (res.response.data?.id) store.addBooking(res.response.data)
    store.closeBookingSlideOver()
  } else {
    error.value = res.response?.message || 'Could not save event.'
  }
}

defineExpose({ form })
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
